import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const DEV = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
const dev = v => String(v).replace(/\d/g, d => DEV[d]);

function hhmm(t) { return String(t || '10:00').slice(0, 5); }
function clock12(t) {
  const [h, m] = hhmm(t).split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')}`;
}
const ampm = t => (Number(hhmm(t).split(':')[0]) < 12 ? 'AM' : 'PM');
const marPart = t => { const h = Number(hhmm(t).split(':')[0]); return h < 12 ? 'सकाळी' : h < 16 ? 'दुपारी' : 'संध्याकाळी'; };

export function useLatePolicyStatus() {
  const { user, isOwner, isAccountant } = useAuth();
  return useQuery({
    queryKey: ['late-policy-status'],
    queryFn: () => api.get('/attendance-sessions/late-policy/status').then(r => r.data),
    enabled: !!user && !isOwner && !isAccountant,
  });
}

// English + Marathi explanation of the late-arrival deduction, built from the live policy values.
function LatePolicyModal({ status, requireAck, onAck, onClose }) {
  const n = status.lates_per_halfday || 3;
  const grace = status.grace_minutes ?? 15;
  const start = status.start || '10:00';
  const lateAfter = status.late_after || '10:15';
  const sec = { marginBottom: 6, lineHeight: 1.5, fontSize: 13 };

  return (
    <div className="modal-overlay" onClick={requireAck ? undefined : onClose}>
      <div className="modal" style={{ maxWidth: 540, maxHeight: '88vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">⏰ Late Arrival Deduction — How It Works</div>
        <div style={sec}>
          Your shift starts at <strong>{clock12(start)} {ampm(start)}</strong>, with a {grace}-minute grace period.
          Clocking in after <strong>{clock12(lateAfter)} {ampm(lateAfter)}</strong> counts as “late” for that day.
        </div>
        <div style={{ ...sec, background: 'var(--accent-light)', padding: '8px 12px', borderRadius: 6 }}>
          <strong>Rule:</strong> every {n} late days in a month = ½ day’s salary deducted.
        </div>
        <div style={sec}><strong>Example:</strong> {n} late days → 0.5 day deducted. {n * 2} late days → 1 full day deducted.</div>
        {n > 1 && <div style={sec}><strong>Tip:</strong> 1–{n - 1} late day{n > 2 ? 's' : ''} in a month cost nothing — only patterns of lateness are deducted.</div>}

        <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0' }} />

        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>⏰ उशिरा येण्याची कपात — हे कसे काम करते</div>
        <div style={sec}>
          तुमची शिफ्ट {marPart(start)} <strong>{dev(clock12(start))}</strong> वाजता सुरू होते, आणि {dev(grace)} मिनिटांची सवलत आहे.
          {' '}<strong>{dev(clock12(lateAfter))}</strong> नंतर हजर झाल्यास तो दिवस “उशीर” म्हणून मोजला जातो.
        </div>
        <div style={{ ...sec, background: 'var(--accent-light)', padding: '8px 12px', borderRadius: 6 }}>
          <strong>नियम:</strong> महिन्यात दर {dev(n)} उशीर दिवसांसाठी अर्धा दिवसाचा पगार कापला जाईल.
        </div>
        <div style={sec}><strong>उदाहरण:</strong> {dev(n)} उशीर दिवस → ०.५ दिवसाची कपात. {dev(n * 2)} उशीर दिवस → १ पूर्ण दिवसाची कपात.</div>
        {n > 1 && <div style={sec}><strong>टीप:</strong> महिन्यात १–{dev(n - 1)} उशीर दिवसांसाठी कोणतीही कपात होत नाही — फक्त सतत उशीर झाल्यासच कपात होते.</div>}

        <div className="modal-actions" style={{ marginTop: 16 }}>
          {requireAck
            ? <button className="btn-primary" onClick={onAck}>I Understand / समजले</button>
            : <button className="btn-primary" onClick={onClose}>Close / बंद करा</button>}
        </div>
      </div>
    </div>
  );
}

// Shown once to each team member, the first time they open the app after the deduction is on.
export default function LatePolicyGate() {
  const qc = useQueryClient();
  const { data } = useLatePolicyStatus();
  const ack = useMutation({
    mutationFn: () => api.post('/attendance-sessions/late-policy/ack'),
    onSuccess: () => qc.invalidateQueries(['late-policy-status']),
  });
  if (!data?.enabled || data.acknowledged) return null;
  return <LatePolicyModal status={data} requireAck onAck={() => ack.mutate()} />;
}

// Permanent ⓘ button — reopens the same explanation any time.
export function LatePolicyInfoButton() {
  const { data } = useLatePolicyStatus();
  const [open, setOpen] = useState(false);
  if (!data?.enabled) return null;
  return (
    <>
      <button type="button" className="btn-ghost btn-sm" onClick={() => setOpen(true)}
        title="Late arrival deduction — how it works" style={{ fontSize: 12 }}>
        ⓘ Late policy
      </button>
      {open && <LatePolicyModal status={data} onClose={() => setOpen(false)} />}
    </>
  );
}
